import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { isTrailUnlocked, unlockTrail } from '../../services/studentUnlockService';
import { startTrail } from '../../services/studentProgressService';
import { supabase } from '../../lib/supabase';

const QRCodeScanner = () => {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const scannerInstanceRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [cameraPermission, setCameraPermission] = useState('prompt');
  const [scannedData, setScannedData] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const studentSession = useRef(null);

  useEffect(() => {
    const session = localStorage.getItem('studentSession');
    if (!session) {
      navigate('/student-login', { replace: true });
      return;
    }
    try {
      studentSession.current = JSON.parse(session);
    } catch (e) {
      navigate('/student-login', { replace: true });
      return;
    }

    // Dynamically import html5-qrcode to avoid SSR issues
    initializeScanner();

    return () => {
      stopScanner();
    };
  }, [navigate]);

  const stopScanner = () => {
    if (scannerInstanceRef?.current) {
      try {
        scannerInstanceRef?.current?.stop()?.catch(() => {});
        scannerInstanceRef?.current?.clear();
      } catch (e) {
        // ignore cleanup errors
      }
      scannerInstanceRef.current = null;
    }
  };

  const initializeScanner = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');

      // Wait for the DOM element to be available
      await new Promise(resolve => setTimeout(resolve, 300));

      if (!scannerRef?.current) return;

      const html5QrCode = new Html5Qrcode('qr-reader');
      scannerInstanceRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        disableFlip: false,
      };

      await html5QrCode?.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => handleScanSuccess(decodedText),
        (errorMessage) => {
          // Ignore scan errors (they fire constantly when no QR in frame)
        }
      );

      setScanning(true);
      setCameraPermission('granted');
      setError(null);
    } catch (err) {
      console.error('Scanner initialization error:', err);
      const errStr = err?.toString() || '';
      const isDismissed =
        errStr?.includes('Permission dismissed') ||
        errStr?.includes('dismissed') ||
        (err?.name === 'NotAllowedError' && errStr?.includes('dismissed'));

      if (isDismissed) {
        // User closed the permission dialog — they can try again
        setCameraPermission('dismissed');
        setError('Camera permission was dismissed. Tap "Allow Camera" to try again.');
      } else if (err?.name === 'NotAllowedError' || errStr?.includes('NotAllowedError')) {
        setCameraPermission('denied');
        setError('Camera access denied. Please enable camera permissions in your browser settings.');
      } else if (err?.name === 'NotFoundError' || errStr?.includes('NotFoundError')) {
        setCameraPermission('denied');
        setError('No camera found on this device. Please use manual entry.');
      } else {
        setCameraPermission('dismissed');
        setError('Failed to initialize camera. Please try again.');
      }
      setScanning(false);
    }
  };

  const handleScanSuccess = async (decodedText) => {
    if (!decodedText || processing) return;

    // Extract trailId from scanned URL
    const urlMatch = decodedText?.match(/[?&]trailId=([a-f0-9-]+)/i);
    if (!urlMatch || !urlMatch?.[1]) {
      setError('Invalid QR code. Please scan a valid learning trail QR code.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    let trailId = urlMatch?.[1];
    setProcessing(true);
    stopScanner();
    setScanning(false);

    await processTrailAccess(trailId);
  };

  const processTrailAccess = async (trailId) => {
    const session = studentSession?.current;
    if (!session?.id) {
      navigate('/student-login', { replace: true });
      return;
    }

    try {
      setStatusMessage('Checking trail...');

      // --- Grade Level Gate ---
      // Fetch trail's min_grade_level
      const { data: trailData, error: trailFetchError } = await supabase
        ?.from('learning_trails')
        ?.select('min_grade_level')
        ?.eq('id', trailId)
        ?.maybeSingle();

      if (!trailFetchError && trailData) {
        const minGrade = trailData?.min_grade_level || 'All Grades';

        if (minGrade !== 'All Grades') {
          // Fetch student's classroom grade_level
          const { data: studentData, error: studentFetchError } = await supabase
            ?.from('students')
            ?.select('classroom_id, classrooms(grade_level)')
            ?.eq('id', session?.id)
            ?.maybeSingle();

          if (!studentFetchError && studentData) {
            const studentGrade = studentData?.classrooms?.grade_level;

            const gradeOrder = {
              'Primary 1': 1,
              'Primary 2': 2,
              'Primary 3': 3,
              'Primary 4': 4,
              'Primary 5': 5,
              'Primary 6': 6,
            };

            const studentGradeNum = gradeOrder?.[studentGrade] || 0;
            const minGradeNum = gradeOrder?.[minGrade] || 0;

            if (studentGradeNum < minGradeNum) {
              // Block access
              setProcessing(false);
              setStatusMessage('');
              setError(`This trail requires ${minGrade} or above. Keep it up and you'll get there!`);
              // Restart scanner after a delay
              setTimeout(() => {
                setError(null);
                initializeScanner();
              }, 5000);
              return;
            }
          }
        }
      }
      // --- End Grade Level Gate ---

      // Check if already unlocked
      const { unlocked, error: checkError } = await isTrailUnlocked(session?.id, trailId);

      if (checkError) {
        // If table doesn't exist yet, still allow access
        console.warn('Unlock check error:', checkError);
      }

      if (!unlocked) {
        setStatusMessage('Unlocking trail...');
        // Unlock the trail
        const { success, error: unlockError } = await unlockTrail(session?.id, trailId);
        if (!success && unlockError) {
          console.error('Failed to unlock trail:', unlockError);
          // Still proceed to trail even if unlock record fails
        }
      }

      // Start trail progress record
      setStatusMessage('Opening trail...');
      await startTrail(session?.id, trailId);

      setScannedData({ trailId });
      setStatusMessage('');

      // Navigate to trail after brief success display
      setTimeout(() => {
        navigate(`/student-learning-trail-view?trailId=${trailId}`);
      }, 1200);
    } catch (err) {
      console.error('processTrailAccess error:', err);
      setError('Something went wrong. Please try again.');
      setProcessing(false);
      setStatusMessage('');
    }
  };

  const handleManualSubmit = async (e) => {
    e?.preventDefault();
    if (!manualCode?.trim() || processing) return;

    let trailId = null;

    // Try to extract trail ID from URL
    const urlMatch = manualCode?.match(/[?&]trailId=([a-f0-9-]+)/i);
    if (urlMatch && urlMatch?.[1]) {
      trailId = urlMatch?.[1];
    } else if (/^[a-f0-9-]{36}$/i?.test(manualCode?.trim())) {
      trailId = manualCode?.trim();
    } else {
      setError('Invalid trail code. Please enter a valid trail ID or URL.');
      return;
    }

    setProcessing(true);
    await processTrailAccess(trailId);
  };

  const handleRetry = () => {
    setScannedData(null);
    setError(null);
    setProcessing(false);
    setStatusMessage('');
    setCameraPermission('prompt');
    initializeScanner();
  };

  const handleBackToDashboard = () => {
    stopScanner();
    navigate('/student-dashboard');
  };

  return (
    <>
      <Helmet>
        <title>QR Code Scanner - LearningTrails</title>
        <meta name="description" content="Scan QR codes to unlock learning trails" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={handleBackToDashboard}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
              <h1 className="text-lg font-semibold text-foreground">QR Scanner</h1>
              <button
                onClick={() => {
                  if (!showManualEntry) stopScanner();
                  else initializeScanner();
                  setShowManualEntry(!showManualEntry);
                  setError(null);
                }}
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                {showManualEntry ? 'Scan QR' : 'Manual Entry'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-2xl mx-auto">
            {!showManualEntry ? (
              <>
                {/* Scanner View */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* Camera Preview */}
                  <div className="relative bg-black">
                    {/* html5-qrcode renders into this div */}
                    <div
                      id="qr-reader"
                      ref={scannerRef}
                      className="w-full"
                      style={{ minHeight: '300px' }}
                    />

                    {/* Camera Permission Prompt */}
                    {cameraPermission === 'prompt' && !scanning && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                        <div className="text-center text-white p-6">
                          <Icon name="Camera" size={48} className="mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">Starting Camera...</h3>
                          <p className="text-sm text-white/80 mb-4">
                            Allow camera access when prompted to scan QR codes.
                          </p>
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto" />
                        </div>
                      </div>
                    )}

                    {/* Camera Permission Dismissed — show retry */}
                    {cameraPermission === 'dismissed' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                        <div className="text-center text-white p-6">
                          <Icon name="Camera" size={48} className="mx-auto mb-4 text-yellow-400" />
                          <h3 className="text-lg font-semibold mb-2">Camera Permission Needed</h3>
                          <p className="text-sm text-white/80 mb-6">
                            Please allow camera access when your browser asks, so you can scan QR codes.
                          </p>
                          <div className="flex flex-col gap-3">
                            <Button
                              onClick={() => {
                                setCameraPermission('prompt');
                                setError(null);
                                initializeScanner();
                              }}
                              className="bg-white text-black hover:bg-white/90"
                            >
                              Allow Camera
                            </Button>
                            <Button
                              onClick={() => { setShowManualEntry(true); setError(null); }}
                              variant="outline"
                              className="border-white text-white hover:bg-white/10"
                            >
                              Use Manual Entry
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Camera Permission Denied */}
                    {cameraPermission === 'denied' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                        <div className="text-center text-white p-6">
                          <Icon name="CameraOff" size={48} className="mx-auto mb-4 text-red-400" />
                          <h3 className="text-lg font-semibold mb-2">
                            {error?.includes('No camera') ? 'No Camera Found' : 'Camera Access Denied'}
                          </h3>
                          <p className="text-sm text-white/80 mb-4">
                            {error || 'Please enable camera permissions in your browser settings.'}
                          </p>
                          <Button onClick={() => { setShowManualEntry(true); setError(null); }} variant="outline">
                            Use Manual Entry
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Success State */}
                    {scannedData && (
                      <div className="absolute inset-0 flex items-center justify-center bg-green-600/90 z-10">
                        <div className="text-center text-white p-6">
                          <Icon name="CheckCircle2" size={64} className="mx-auto mb-4" />
                          <h3 className="text-xl font-bold mb-2">Trail Unlocked! 🎉</h3>
                          <p className="text-sm text-white/90">
                            Opening your learning trail...
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Processing State */}
                    {processing && !scannedData && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10">
                        <div className="text-center text-white p-6">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
                          <p className="text-sm text-white/90">{statusMessage || 'Processing...'}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Instructions */}
                  <div className="p-6">
                    {error && cameraPermission !== 'denied' && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                        <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}

                    {scanning && !scannedData && !processing && (
                      <div className="space-y-4">
                        <div className="text-center">
                          <h3 className="text-lg font-semibold text-foreground mb-2">Position QR Code in Frame</h3>
                          <p className="text-sm text-muted-foreground">
                            Hold your device steady and point the camera at the QR code
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                            <Icon name="Lightbulb" size={16} className="mr-2 text-blue-500" />
                            Scanning Tips
                          </h4>
                          <ul className="space-y-1 text-xs text-muted-foreground">
                            <li className="flex items-start">
                              <Icon name="Check" size={14} className="mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>Ensure good lighting for best results</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={14} className="mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>Hold device 6–8 inches from QR code</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={14} className="mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>Keep QR code flat and avoid glare</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {!scanning && !scannedData && !processing && cameraPermission !== 'denied' && (
                      <div className="text-center">
                        <Button onClick={handleRetry} iconName="RefreshCw">
                          Retry Camera
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* Manual Entry View */
              (<div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    <Icon name="Keyboard" size={32} className="text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Manual Entry</h2>
                  <p className="text-sm text-muted-foreground">
                    Enter the trail code or URL provided by your teacher
                  </p>
                </div>
                <form onSubmit={handleManualSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="manualCode" className="block text-sm font-medium text-foreground mb-2">
                      Trail Code or URL
                    </label>
                    <input
                      id="manualCode"
                      type="text"
                      value={manualCode}
                      onChange={(e) => setManualCode(e?.target?.value)}
                      placeholder="Enter trail ID or paste URL"
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                      <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <Button type="submit" fullWidth iconName="ArrowRight" disabled={processing}>
                    {processing ? 'Processing...' : 'Access Trail'}
                  </Button>
                </form>
                <div className="mt-6 pt-6 border-t border-border">
                  <button
                    onClick={() => {
                      setShowManualEntry(false);
                      setError(null);
                      initializeScanner();
                    }}
                    className="w-full text-sm text-primary hover:text-primary/80 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Icon name="Camera" size={16} />
                    <span>Switch to QR Scanner</span>
                  </button>
                </div>
              </div>)
            )}
          </div>
        </div>
      </div>
      <style>{`
        #qr-reader {
          border: none !important;
        }
        #qr-reader video {
          width: 100% !important;
          border-radius: 0 !important;
        }
        #qr-reader__scan_region {
          background: transparent !important;
        }
        #qr-reader__dashboard {
          display: none !important;
        }
      `}</style>
    </>
  );
};

export default QRCodeScanner;